import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ParkingStats = () => {
  const [timeRange, setTimeRange] = useState('daily');
  const [statsData, setStatsData] = useState({
    totalRevenue: 0,
    totalVehicles: 0,
    averageStay: 0,
    occupancyRate: 0,
  });
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [timeRange]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const now = new Date();
      let startDate = new Date();

      switch (timeRange) {
        case 'daily':
          startDate.setHours(0, 0, 0, 0);
          break;
        case 'weekly':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'monthly':
          startDate.setMonth(now.getMonth() - 1);
          break;
        default:
          startDate.setHours(0, 0, 0, 0);
      }

      const parkingRef = collection(db, 'parkingRecords');
      const q = query(parkingRef, where('timestamp', '>=', startDate), orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(q);
      const records = [];
      querySnapshot.forEach((doc) => {
        records.push({ id: doc.id, ...doc.data() });
      });

      const totalRevenue = records.reduce((sum, record) => sum + (record.amount || 0), 0);
      const totalVehicles = records.length;

      generateChartData(records, timeRange);

      setStatsData({
        totalRevenue,
        totalVehicles,
        averageStay: calculateAverageStay(records),
        occupancyRate: calculateOccupancyRate(records),
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAverageStay = (records) => {
    if (records.length === 0) return 0;
    const totalHours = records.reduce((sum, record) => {
      if (record.checkIn && record.checkOut) {
        const duration = (record.checkOut.toDate() - record.checkIn.toDate()) / (1000 * 60 * 60);
        return sum + duration;
      }
      return sum;
    }, 0);
    return (totalHours / records.length).toFixed(1);
  };

  const calculateOccupancyRate = (records) => {
    const maxCapacity = 50;
    const currentOccupied = records.filter(r => !r.checkOut).length;
    return ((currentOccupied / maxCapacity) * 100).toFixed(1);
  };

  const generateChartData = (records, range) => {
    let labels = [];
    let data = [];

    if (range === 'daily') {
      labels = Array.from({ length: 24 }, (_, i) => `${i}:00`);
      data = new Array(24).fill(0);
      records.forEach(record => {
        if (record.timestamp) {
          const hour = record.timestamp.toDate().getHours();
          data[hour]++;
        }
      });
    } else if (range === 'weekly') {
      labels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      data = new Array(7).fill(0);
      records.forEach(record => {
        if (record.timestamp) {
          const day = record.timestamp.toDate().getDay();
          data[day]++;
        }
      });
    } else if (range === 'monthly') {
      labels = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
      data = new Array(4).fill(0);
      records.forEach(record => {
        if (record.timestamp) {
          const weekOfMonth = Math.floor(record.timestamp.toDate().getDate() / 7);
          if (weekOfMonth < 4) {
            data[weekOfMonth]++;
          }
        }
      });
    }

    setChartData({
      labels,
      datasets: [{
        label: 'Vehicles',
        data,
        backgroundColor: 'rgba(59, 130, 246, 0.6)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
      }],
    });
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: {
        display: true,
        text: `Parking Activity - ${timeRange.charAt(0).toUpperCase() + timeRange.slice(1)}`,
      },
    },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading statistics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-3">
            <h2 className="text-2xl font-bold text-gray-900">Parking Statistics</h2>
            <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-md">Square Data</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {['daily', 'weekly', 'monthly'].map(range => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  timeRange === range
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {range.charAt(0).toUpperCase() + range.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 uppercase">Total Revenue</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                ${statsData.totalRevenue.toFixed(2)}
              </p>
            </div>
            <div className="bg-green-100 rounded-full p-3">
              <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="32" height="32">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 uppercase">Total Vehicles</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">{statsData.totalVehicles}</p>
            </div>
            <div className="bg-blue-100 rounded-full p-3">
              <svg className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="32" height="32">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 uppercase">Average Stay</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">{statsData.averageStay}h</p>
            </div>
            <div className="bg-purple-100 rounded-full p-3">
              <svg className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="32" height="32">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 uppercase">Occupancy Rate</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">{statsData.occupancyRate}%</p>
            </div>
            <div className="bg-orange-100 rounded-full p-3">
              <svg className="h-8 w-8 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="32" height="32">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      {chartData && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="h-96">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ParkingStats;
